// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/AskPunttArtworkViewer.test.tsx
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  buildCommentNumberById,
  reviewCommentTitle,
} from "./reviewCommentDisplay";

import {
  pastRejectionReceipt,
  previouslyRejectedReceiptLabel,
} from "../../ask-v2/review/AskV2ReviewJudgmentReceipt";
import {
  AssetViewerPane,
  type AssetFile,
  type AssetViewerPaneHandle,
  type HumanCommentSubmitInput,
} from "../../routes/ask-puntt/components/AssetViewerPane";
import {
  PinToneContext,
  type PinTone,
} from "../../routes/ask-puntt/components/pinToneContext";
import {
  RejectedPinLabelContext,
  type RejectedPinKind,
} from "../../routes/ask-puntt/components/rejectedPinLabelContext";

type AskPunttArtworkViewerProps = {
  reviewId: string;
  title: string;
  files: AssetFile[];
  comments: readonly AskPunttReviewComment[];
  activeCommentId?: string | null;
  onHeaderControlsChange?: (controls: React.ReactNode | null) => void;
  onCommentClick?: (comment: AskPunttReviewComment) => void;
  onArtworkBackgroundClick?: () => void;
  /**
   * When provided, a bare click on the artwork drops a draft pin and opens the
   * inline compose bubble; the typed comment plus its pin location land here.
   */
  onSubmitHumanComment?: (input: HumanCommentSubmitInput) => Promise<void>;
  /**
   * Each finding's number, by id, as the findings list prints it. When left
   * out, the pins are numbered over `comments` in order.
   */
  commentNumbers?: Readonly<Record<string, number>>;
  /** Lets a pin rejected in an earlier review say "You rejected this before". */
  currentUserId?: string;
  /** Colors a pin by the side of a version comparison it stands for. */
  pinTone?: (comment: BaseTicketComment) => PinTone | null;
};

export const AskPunttArtworkViewer = forwardRef<
  AssetViewerPaneHandle,
  AskPunttArtworkViewerProps
>(function AskPunttArtworkViewer(
  {
    reviewId,
    title,
    files,
    comments,
    activeCommentId = null,
    onHeaderControlsChange,
    onCommentClick,
    onArtworkBackgroundClick,
    onSubmitHumanComment,
    commentNumbers,
    currentUserId,
    pinTone,
  },
  ref,
) {
  const { t } = useTranslation("ui");
  // why: a finding rejected in this review, or in an earlier one, keeps its
  // pin, grayed and named as rejected; the v1 panes never provide this, so
  // their pins are unchanged.
  const rejectedPinLabel = useCallback(
    (comment: BaseTicketComment, kind: RejectedPinKind) => {
      const reviewComment = comment as AskPunttReviewComment;
      const issue = reviewCommentTitle(reviewComment);
      const receipt = pastRejectionReceipt(reviewComment);
      if (kind === "before" && receipt) {
        return t("askV2.review.rejectedBeforePinLabel", {
          receipt: previouslyRejectedReceiptLabel(t, receipt, currentUserId),
          issue,
        });
      }
      return t("askV2.review.rejectedPinLabel", { issue });
    },
    [currentUserId, t],
  );
  const stableComments = useMemo(
    () =>
      comments.filter((comment) => !comment.isDeleted) as BaseTicketComment[],
    [comments],
  );
  const commentNumberById = useMemo(
    () => commentNumbers ?? buildCommentNumberById(comments),
    [commentNumbers, comments],
  );

  const handleCommentClick = onCommentClick
    ? (comment: BaseTicketComment) =>
        onCommentClick(comment as AskPunttReviewComment)
    : undefined;

  return (
    <RejectedPinLabelContext.Provider value={rejectedPinLabel}>
      <PinToneContext.Provider value={pinTone ?? null}>
        <AssetViewerPane
          key={reviewId}
          ref={ref}
          title={title}
          assets={files}
          comments={stableComments}
          allReviewComments={stableComments}
          activeCommentId={activeCommentId}
          onCommentClick={handleCommentClick}
          onPinClick={handleCommentClick}
          onArtworkBackgroundClick={onArtworkBackgroundClick}
          onSubmitHumanComment={onSubmitHumanComment}
          humanCommentPlacementTrigger={
            onSubmitHumanComment ? "click" : "toggle"
          }
          commentComposePlacement="inline"
          commentNumberById={commentNumberById}
          hideCommentPanel
          hideTitleRow
          viewerToolbarPlacement="external"
          viewerContentInset="inset16"
          onHeaderControlsChange={onHeaderControlsChange}
        />
      </PinToneContext.Provider>
    </RejectedPinLabelContext.Provider>
  );
});
